import { Fee } from './FeeEntity'
import { TradesStat } from './Instrument'
import { InvestorIssue } from './InvestorEntity'
import { SourceEntity } from './SourceEntity'

export type IssueType =
    | 'Rights issue'
    | 'ABB Directed issue'
    | 'Directed issue'
    | 'Non-cash issue'
    | 'Guarantor fee issue'
    | 'IPO'

export type IssueTypeWarrantConvertible = IssueType | 'Warrant' | 'Convertible'

export const IssueTypeWarrantConvertibleShort: Record<IssueTypeWarrantConvertible, string> = {
    'Rights issue': 'FE',
    'Non-cash issue': 'AE',
    IPO: 'IPO',
    'ABB Directed issue': 'ABB',
    'Directed issue': 'RE',
    'Guarantor fee issue': 'GE',
    Warrant: 'TO',
    Convertible: 'KV',
}

export type IssueTypeShortType =
    (typeof IssueTypeWarrantConvertibleShort)[keyof typeof IssueTypeWarrantConvertibleShort]

export type TradeData = {
    avgTurnover: string | null
    avgQuantity: string | null
    endDate: string | null
    isSplitAdjusted: number | null
    startDate: string | null
    totalTurnover: string | null
    totalQuantity: number | null
    vwap: number | null
}

export type Advisor = {
    advisorId: string
    name: string | null
}

type ShareSplit = {
    changeDate: string
    newShares: number
    oldShares: number
}

type BonusIssue = ShareSplit

export type EnrichedIssueEntity = {
    totalOfferedUnits: number | null
    totalUnitSize: number | null
} & IssueEntity

export type IssueEntity = {
    abbTarget: number | null
    abbTargetSek: number | null
    advisors:
        | {
              type: 'financial_advisor' | 'legal_advisor' | 'issuing_agent'
          }[]
        | null
    approxCommitment: string | null
    approxGuarantor: string | null
    approxTotalCommitment: string | null
    bonusIssues: BonusIssue[]
    overAllotmentAllocation: number | null
    cancelled: boolean | null
    companyCountryCode: string | null
    companyId: string | null
    companyName: string | null
    companyRegistrationNumber: string | null
    currency: string
    earnout: 0 | 1 | null
    exRightsDate: string | null
    externalComment: string | null
    fees: Fee[]
    generalMeetingDate: string | null
    hasWarrants: 0 | 1 | null
    id: string
    ipoData: {
        companyName: string | null
        companyTotalShares: number | null
        instrumentIsin: string | null
        instrumentMillistreamInsref: string | null
        marketplaceId: string | null
        shareNumberOfShares: number | null
    } | null
    ipoTradingDate: string | null
    isInWatchList: 0 | 1 | null
    instrumentId: string | null
    instrumentName: string | null
    instrumentSymbol: string | null
    noGuarantors: 0 | 1 | null
    financialAdvisors: Advisor[]
    legalAdvisors: Advisor[]
    linkedIssues: { groupId: string; issues: Omit<IssueEntity, 'linkedIssues'>[] }[]
    marketcap: number | null
    marketplaceId: string
    marketplaceName: string | null
    minimumSubCondition: string | null
    noInvestorsNamed: 0 | 1 | null
    noTermsInitially: number | null
    incRightsDate: string | null
    investors: InvestorIssue[]
    investorsAfterTerms: 0 | 1 | null
    investorsAfterProspectus: 0 | 1 | null
    ipoIntervalHigh: string | null
    ipoIntervalLow: string | null
    isinRights: string | null
    isinUnits: string | null
    noFinancialAdvisor: 0 | 1 | null
    noGreenshoe: boolean | null
    noIsinUnits: 0 | 1 | null
    noIssuingAgent: 0 | 1 | null
    noLegalAdvisor: 0 | 1 | null
    noncashConsideration: string | null
    noOverAllotment: boolean | null
    noSecondaryOffering: boolean | null
    numberOfSharesBeforePublicationDate: number | null
    shareName: string | null
    sector1: string
    sector2: string
    sector3: string
    sectorLevel1: string
    sectorLevel2: string
    shareNameMetadata: {
        currentName: string
        currentNameSince: string
        nameAtPublication: string
    }
    statusComplete: number | null
    statusStage: number | null
    symbol: string | null
    type: IssueType
    offeredUnits: number | null
    offeredUnitsUnlisted: number | null
    overAllotmentSize: number | null
    overAllotmentSizeSecondary: number | null
    overAllotmentAllocationSecondary: number | null
    preMoney: string | null
    publicationDate: string | null
    publishedAt: string | null
    unitsReceived: number | null
    recordDate: string | null
    rightsEndDate: string | null
    rightsStartDate: string | null
    rightsReceived: number | null
    rightsRequired: number | null
    rto: number | null
    secondarySub: number | null
    secondaryOffering: number | null
    shareId: string | null
    sharesInUnit: number | null
    sharesRequired: number | null
    shareSplits: ShareSplit[]
    statusHideUntil: null | string
    sources: SourceEntity[]
    subStartDate: string | null
    subEndDate: string | null
    subscriptionRate: string | null
    termsDate: string | null
    tradesStats: Array<TradesStat>
    unitExtraContent: string | null
    unitInstrumentName: string | null
    unitInstrumentSymbol: string | null
    unitPctSubGuarantor: number | null
    unitPctSubWithoutRights: number | null
    unitPctSubWithRights: number | null
    unitSubGuarantor: number | null
    unitSubWithoutRights: number | null
    unitSubWithRights: number | null
    unitSubPrice: string | null
    unitSubPriceSek: string | null
    unitSubTotal: number | null
    warrants: Array<{
        id: string
        origins: Array<{ issueId: string; numberOfWarrants: number | null }> | null
        warrantsPerUnit: number | null
    }> | null
}
